import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import Checkbox from 'components/shared/form/Checkbox';
import FieldLabel from 'components/shared/form/FieldLabel';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import ToggleGroup, { ToggleOption } from 'components/shared/form/ToggleGroup';
import {
  adminCreateDemoSetupRun,
  adminDemoSetupRunsQueryKeys,
  adminEligibleDemoSetupGroups,
  adminIndexDemoSetupRuns,
  adminShowDemoSetupRun,
  DemoSetupGroupOption,
  DemoSetupRun,
  DemoSetupRunGroupStatus,
  TrainingType,
} from 'services/api/admin/demoSetupRuns';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

const POLL_INTERVAL_MS = 1500;

const STATUS_LABEL: Record<DemoSetupRunGroupStatus, string> = {
  queued: 'Queued',
  in_progress: 'In progress',
  success: 'Success',
  failed: 'Failed',
};

const STATUS_COLOR: Record<DemoSetupRunGroupStatus, string> = {
  queued: colors.black25,
  in_progress: colors.primaryBlue,
  success: colors.actionGreen,
  failed: colors.accentRed,
};

function getOptionId(g: DemoSetupGroupOption) {
  return g.id;
}

// Compact abbreviations for the most common post-acute / owner group types so
// two same-named facilities (e.g. "Guardian Care" SNF vs LTC) can be told apart
// in the dropdown at a glance.
const GROUP_TYPE_ABBR: Record<string, string> = {
  skilled_nursing_facility: 'SNF',
  long_term_care: 'LTC',
  inpatient_rehab_facility: 'IRF',
  long_term_acute_care_hospital: 'LTACH',
  rehab_facility: 'Rehab',
  home_health_agency: 'HHA',
  hospice: 'Hospice',
  hospital: 'Hospital',
  payer: 'Payer',
  physician_group: 'Physician Group',
  snf_management: 'SNF Mgmt',
};

function prettyGroupType(apiName: string | null): string {
  if (!apiName) return '';
  return GROUP_TYPE_ABBR[apiName] ?? apiName.replace(/_/g, ' ');
}

function getOptionName(g: DemoSetupGroupOption) {
  const typeLabel = prettyGroupType(g.groupType);
  const meta = [g.clientName, typeLabel].filter(Boolean).join(' — ');
  return meta ? `${g.name} (${meta})` : g.name;
}

const TRAINING_TYPE_LABEL: Record<TrainingType, string> = {
  snf: 'SNF',
  home_health: 'Home Health',
};

const TRAINING_TYPE_OPTIONS: ToggleOption[] = [
  { label: 'SNF Provider', value: 'snf' },
  { label: 'Home Health', value: 'home_health' },
];

const SUB_UNIT_LABEL: Record<TrainingType, string> = {
  snf: 'Post-Acute Locations',
  home_health: 'Home Health Network Provider',
};

const DemoEnvSetup: React.FC = () => {
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();

  const [trainingType, setTrainingType] = useState<TrainingType>('snf');
  const [selectedOwner, setSelectedOwner] = useState<DemoSetupGroupOption | null>(null);
  const [selectedPostAcutes, setSelectedPostAcutes] = useState<DemoSetupGroupOption[]>([]);
  const [archiveExisting, setArchiveExisting] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  // ---- Owner picker options
  const ownersQuery = useQuery({
    queryKey: adminDemoSetupRunsQueryKeys.eligibleGroups('owner'),
    queryFn: ({ signal }) => adminEligibleDemoSetupGroups({ role: 'owner' }, signal),
  });

  // ---- Sub-unit picker options (depend on selected owner and training type)
  const postAcutesQuery = useQuery({
    queryKey: adminDemoSetupRunsQueryKeys.eligibleGroups('post_acute', selectedOwner?.id, trainingType),
    queryFn: ({ signal }) =>
      adminEligibleDemoSetupGroups({ role: 'post_acute', ownerGroupId: selectedOwner!.id, trainingType }, signal),
    enabled: !!selectedOwner,
  });

  // ---- Submit
  const createMutation = useMutation({
    mutationFn: () =>
      adminCreateDemoSetupRun({
        ownerGroupId: selectedOwner!.id,
        postAcuteGroupIds: selectedPostAcutes.map((g) => g.id),
        trainingType,
        archiveExisting,
      }),
    onSuccess: (run) => {
      setActiveRunId(run.id);
      queryClient.invalidateQueries({ queryKey: adminDemoSetupRunsQueryKeys.index() });
      addToast({ text: `Setup started for ${run.runGroups.length} location(s)` });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Failed to start setup';
      addToast({ text: msg });
    },
  });

  // ---- Active run polling
  const activeRunQuery = useQuery({
    queryKey: activeRunId ? adminDemoSetupRunsQueryKeys.show(activeRunId) : ['adminDemoSetupRuns', 'noop'],
    queryFn: ({ signal }) => adminShowDemoSetupRun(activeRunId!, signal),
    enabled: !!activeRunId,
    refetchInterval: (q) => {
      const data = q.state.data as DemoSetupRun | undefined;
      if (!data) return POLL_INTERVAL_MS;
      const anyPending = data.runGroups.some((rg) => rg.status === 'queued' || rg.status === 'in_progress');
      if (!anyPending) {
        // The run just hit a terminal state. Refresh history exactly once so the
        // run shows up there without needing a manual page refresh, then stop
        // polling. refetchInterval returning `false` halts polling, so this
        // branch only executes on the transition.
        queryClient.invalidateQueries({ queryKey: adminDemoSetupRunsQueryKeys.index() });
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });

  // ---- History
  const historyQuery = useQuery({
    queryKey: adminDemoSetupRunsQueryKeys.index({ page: 1 }),
    queryFn: ({ signal }) => adminIndexDemoSetupRuns({ page: 1, perPage: 25 }, signal),
  });

  const submitDisabled =
    !selectedOwner ||
    selectedPostAcutes.length === 0 ||
    createMutation.isPending ||
    (!!activeRunQuery.data &&
      activeRunQuery.data.runGroups.some((rg) => rg.status === 'queued' || rg.status === 'in_progress'));

  const subtitleText = archiveExisting
    ? 'Archive existing active patients in selected locations, then add training-ready data.'
    : 'Augment selected locations with training-ready data — existing data is left untouched.';

  const selectedTrainingTypeOption =
    TRAINING_TYPE_OPTIONS.find((o) => o.value === trainingType) ?? TRAINING_TYPE_OPTIONS[0];

  return (
    <Page>
      <PageHeader>
        <h1>Demo Env Setup</h1>
        <PageSubtitle>{subtitleText}</PageSubtitle>
      </PageHeader>

      <Card>
        <CardTitle>New Setup Run</CardTitle>

        <Field>
          <FieldLabel>Training Type</FieldLabel>
          <ToggleGroup
            options={TRAINING_TYPE_OPTIONS}
            selectedOption={selectedTrainingTypeOption}
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => o.value}
            disabled={false}
            onChange={(option) => {
              setTrainingType(option.value as TrainingType);
              setSelectedPostAcutes([]);
            }}
          />
        </Field>

        <Field>
          <FieldLabel>Owner</FieldLabel>
          <Select
            options={ownersQuery.data ?? []}
            getOptionValue={getOptionId}
            getOptionLabel={getOptionName}
            value={selectedOwner}
            onChange={(g) => {
              setSelectedOwner(g);
              setSelectedPostAcutes([]); // owner change resets post-acute selection
            }}
            placeholder={ownersQuery.isLoading ? 'Loading owners...' : 'Select an owner'}
            disabled={ownersQuery.isLoading}
            dataTestId='demo-env-setup-owner'
          />
        </Field>

        <Field>
          <MultiSelectChips
            title={SUB_UNIT_LABEL[trainingType]}
            options={postAcutesQuery.data ?? []}
            getOptionValue={getOptionId}
            getOptionLabel={getOptionName}
            values={selectedPostAcutes}
            onChange={setSelectedPostAcutes}
            placeholder={
              !selectedOwner
                ? 'Select an owner first'
                : postAcutesQuery.isLoading
                  ? 'Loading locations...'
                  : 'Select locations'
            }
            disabled={!selectedOwner || postAcutesQuery.isLoading}
          />
        </Field>

        <Field>
          <Checkbox
            checked={archiveExisting}
            onChange={setArchiveExisting}
            label='Archive existing patients before setup'
          />
        </Field>

        <SubmitRow>
          <Button onClick={() => createMutation.mutate()} disabled={submitDisabled} loading={createMutation.isPending}>
            Set Up Training Data
          </Button>
        </SubmitRow>
      </Card>

      {activeRunQuery.data && (
        <Card>
          <CardTitle>Run Progress</CardTitle>
          <ProgressTable run={activeRunQuery.data} />
        </Card>
      )}

      <Card>
        <CardTitle>History</CardTitle>
        <RunHistory runs={historyQuery.data?.data ?? []} loading={historyQuery.isLoading} />
      </Card>
    </Page>
  );
};

// ----------- Sub-components -----------

const ProgressTable: React.FC<{ run: DemoSetupRun }> = ({ run }) => (
  <Table>
    <thead>
      <tr>
        <Th>Location</Th>
        <Th>Status</Th>
        <Th>Result</Th>
      </tr>
    </thead>
    <tbody>
      {run.runGroups.map((rg) => (
        <tr key={rg.id}>
          <Td>{rg.group.name}</Td>
          <Td>
            <StatusPill status={rg.status}>{STATUS_LABEL[rg.status]}</StatusPill>
          </Td>
          <Td>
            {rg.status === 'success' && (
              <>
                <CountSummary counts={rg.itemCounts} />
                <ArchivedSummary counts={rg.itemCounts} />
                <SkippedList warnings={rg.warnings} />
              </>
            )}
            {rg.status === 'failed' && <ErrorMessage>{rg.errorMessage}</ErrorMessage>}
            {(rg.status === 'queued' || rg.status === 'in_progress') && <Muted>—</Muted>}
          </Td>
        </tr>
      ))}
    </tbody>
  </Table>
);

// Lists demo items the backend skipped (e.g. shared records or inbound
// referrals whose dependencies aren't seeded in this environment) so the
// trainer knows what didn't get set up.
const SkippedList: React.FC<{ warnings?: string[] }> = ({ warnings }) => {
  if (!warnings || warnings.length === 0) return null;
  return (
    <SkippedNote>
      <strong>Not set up:</strong>
      <ul>
        {warnings.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </SkippedNote>
  );
};

// Shows what the "Archive existing patients" option removed before seeding,
// so the trainer can confirm it ran (and see when there was nothing to archive).
const ArchivedSummary: React.FC<{ counts: Record<string, number> }> = ({ counts }) => {
  const patients = pick(counts, 'archived_patients', 'archivedPatients');
  const referrals = pick(counts, 'archived_referrals', 'archivedReferrals');
  if (patients === 0 && referrals === 0) return null;
  return (
    <ArchivedNote>
      Archived before setup: {patients} patient{patients === 1 ? '' : 's'}, {referrals} referral
      {referrals === 1 ? '' : 's'}
    </ArchivedNote>
  );
};

// Defensive read: tolerate either snake_case (Rails default) or camelCase
// (if a serializer transforms nested hashes) for the count keys.
function pick(counts: Record<string, number>, ...keys: string[]): number {
  for (const k of keys) {
    if (counts[k] != null) return counts[k];
  }
  return 0;
}

function hasArchiveInfo(counts: Record<string, number>): boolean {
  return (
    pick(counts, 'archived_patients', 'archivedPatients') > 0 ||
    pick(counts, 'archived_referrals', 'archivedReferrals') > 0
  );
}

const CountSummary: React.FC<{ counts: Record<string, number> }> = ({ counts }) => {
  const patients = pick(counts, 'patients');
  const queued = pick(counts, 'patients_queued', 'patientsQueued');
  const inTreatment = pick(counts, 'patients_in_treatment', 'patientsInTreatment');
  const discharged = pick(counts, 'patients_discharged', 'patientsDischarged');
  const red = pick(counts, 'red');
  const yellow = pick(counts, 'yellow');
  const green = pick(counts, 'green');
  const referralsNew = pick(counts, 'referrals_new', 'referralsNew');
  const referralsAccepted = pick(counts, 'referrals_accepted', 'referralsAccepted');
  const referralsDeclined = pick(counts, 'referrals_declined', 'referralsDeclined');
  const referralsDischarge = pick(counts, 'referrals_discharge', 'referralsDischarge');
  const priorityNotes = pick(counts, 'priority_notes', 'priorityNotes');
  const referralTotal = referralsNew + referralsAccepted + referralsDeclined + referralsDischarge;

  // If state-breakdown keys are present (new shape), include them; otherwise fall
  // back to the flat patients count (older runs predating the cohort split).
  const patientsLabel =
    queued + inTreatment + discharged > 0
      ? `${patients} patients (${queued} queue, ${inTreatment} in treatment, ${discharged} discharged)`
      : `${patients} patients`;

  const parts = [
    patientsLabel,
    `${red}🔴 ${yellow}🟡 ${green}🟢`,
    `${referralTotal} referrals (${referralsNew} new, ${referralsAccepted} accepted, ${referralsDeclined} declined, ${referralsDischarge} discharge)`,
    `${priorityNotes} priority notes`,
  ];
  return <span>{parts.join(' · ')}</span>;
};

type HistoryRow = {
  run: DemoSetupRun;
  rg: DemoSetupRun['runGroups'][number];
};

// The history table only surfaces runs from the last 7 days — older demo
// runs aren't actionable for the trainer (they're either already validated
// or long since archived/overwritten), and keeping the table short makes the
// page navigable.
const HISTORY_WINDOW_DAYS = 7;

const RunHistory: React.FC<{ runs: DemoSetupRun[]; loading: boolean }> = ({ runs, loading }) => {
  const recentRuns = useMemo(() => {
    const cutoff = Date.now() - HISTORY_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return runs.filter((run) => new Date(run.createdAt).getTime() >= cutoff);
  }, [runs]);

  const grouped = useMemo(() => {
    const byDate: Record<string, HistoryRow[]> = {};
    for (const run of recentRuns) {
      const date = new Date(run.createdAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      byDate[date] = byDate[date] || [];
      for (const rg of run.runGroups) {
        byDate[date].push({ run, rg });
      }
    }
    return byDate;
  }, [recentRuns]);

  if (loading) return <Muted>Loading history…</Muted>;
  if (recentRuns.length === 0) return <Muted>No previous runs in the last {HISTORY_WINDOW_DAYS} days.</Muted>;

  return (
    <>
      {Object.entries(grouped).map(([date, rows]) => (
        <DateGroup key={date}>
          <DateHeader>{date}</DateHeader>
          <HistoryTable>
            <thead>
              <tr>
                <Th>Time</Th>
                <Th>Triggered By</Th>
                <Th>Type</Th>
                <Th>Owner</Th>
                <Th>Location</Th>
                <Th>Status</Th>
                <ThNum>Patients</ThNum>
                <ThNum title='Red / Yellow / Green status mix'>🔴 🟡 🟢</ThNum>
                <ThNum title='Total (new / accepted / declined)'>Referrals</ThNum>
                <ThNum>Priority Notes</ThNum>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ run, rg }) => (
                <HistoryTableRow key={rg.id} run={run} rg={rg} />
              ))}
            </tbody>
          </HistoryTable>
        </DateGroup>
      ))}
    </>
  );
};

const HistoryTableRow: React.FC<HistoryRow> = ({ run, rg }) => {
  const time = new Date(run.createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const c = rg.itemCounts;
  const success = rg.status === 'success';
  const referralTotal = success
    ? pick(c, 'referrals_new', 'referralsNew') +
      pick(c, 'referrals_accepted', 'referralsAccepted') +
      pick(c, 'referrals_declined', 'referralsDeclined')
    : 0;
  const referralBreakdown = success
    ? `${pick(c, 'referrals_new', 'referralsNew')}/${pick(c, 'referrals_accepted', 'referralsAccepted')}/${pick(
        c,
        'referrals_declined',
        'referralsDeclined'
      )}`
    : '';
  const runTrainingType: TrainingType = run.trainingType ?? 'snf';
  return (
    <tr>
      <Td>{time}</Td>
      <Td>{run.triggeredBy.name}</Td>
      <Td>{TRAINING_TYPE_LABEL[runTrainingType]}</Td>
      <Td>{run.ownerGroup.name}</Td>
      <Td>{rg.group.name}</Td>
      <Td>
        <StatusPill status={rg.status}>{STATUS_LABEL[rg.status]}</StatusPill>
      </Td>
      <TdNum>{success ? pick(c, 'patients') : <Muted>—</Muted>}</TdNum>
      <TdNum>{success ? `${pick(c, 'red')} / ${pick(c, 'yellow')} / ${pick(c, 'green')}` : <Muted>—</Muted>}</TdNum>
      <TdNum title={success ? 'new / accepted / declined' : undefined}>
        {success ? (
          <span>
            {referralTotal} <SubText>({referralBreakdown})</SubText>
          </span>
        ) : (
          <Muted>—</Muted>
        )}
      </TdNum>
      <TdNum>{success ? pick(c, 'priority_notes', 'priorityNotes') : <Muted>—</Muted>}</TdNum>
      <Td>
        {rg.status === 'failed' && <ErrorMessage>{rg.errorMessage}</ErrorMessage>}
        {rg.status !== 'failed' && rg.status !== 'success' && <Muted>processing…</Muted>}
        {success && <ArchivedSummary counts={c} />}
        {success && rg.warnings?.length > 0 && <SkippedList warnings={rg.warnings} />}
        {success && (rg.warnings?.length ?? 0) === 0 && !hasArchiveInfo(c) && <Muted>—</Muted>}
      </Td>
    </tr>
  );
};

// ----------- Styles -----------

const Page = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  h1 {
    margin: 0;
  }
`;

const PageSubtitle = styled.p`
  color: ${colors.black75};
  margin: 8px 0 0;
`;

const Card = styled.section`
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  padding: 24px;
`;

const CardTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 18px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const SubmitRow = styled.div`
  margin-top: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-weight: 600;
  padding: 8px;
  border-bottom: 1px solid ${colors.black10};
  font-size: 12px;
  color: ${colors.black75};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ThNum = styled(Th)`
  text-align: right;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${colors.black05};
  vertical-align: top;
`;

const TdNum = styled(Td)`
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const SubText = styled.span`
  color: ${colors.black50};
  font-size: 12px;
`;

const StatusPill = styled.span<{ status: DemoSetupRunGroupStatus }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: ${colors.white};
  background: ${(p) => STATUS_COLOR[p.status]};
`;

const ErrorMessage = styled.div`
  color: ${colors.accentRed};
  font-size: 13px;
`;

const SkippedNote = styled.div`
  color: ${colors.black75};
  font-size: 12px;
  margin-top: 4px;

  ul {
    margin: 2px 0 0;
    padding-left: 16px;
  }
`;

const ArchivedNote = styled.div`
  color: ${colors.black75};
  font-size: 12px;
  margin-top: 4px;
`;

const Muted = styled.span`
  color: ${colors.black50};
  font-style: italic;
`;

const DateGroup = styled.div`
  margin-bottom: 24px;
`;

const DateHeader = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.black75};
  margin: 16px 0 8px;
  padding-bottom: 4px;
  border-bottom: 2px solid ${colors.black10};
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

export default DemoEnvSetup;
